import { Application, ApplicationAction } from '@features/applications';
import ApplicationViewModal from '@features/applications/components/ApplicationViewModal/ApplicationViewModal';
import { applicationActions } from '@features/applications/components/BulkActions/constants';
import {
  getSendWithMessageButtonText,
  getSendWithoutMessageButtonText,
  getTitle,
} from '@features/applications/components/BulkActions/utils';
import ChooseInterviewStyleModal from '@features/applications/components/ChooseInterviewStyleModal/ChooseInterviewStyleModal';
import SingleMessageModal from '@features/applications/components/SingleMessageModal/SingleMessageModal';
import { useEnterpriseBulkAction } from '@features/applications/hooks/useEnterpriseBulkAction';
import { useInviteToInterview } from '@features/applications/hooks/useInviteToInterview';
import { useInviteToInterviewBlocks } from '@features/applications/hooks/useInviteToInterviewBlocks';
import { InterviewBlock } from '@features/enterpriseEvents';
import { Interview, UserEvent } from '@features/events';
import { getTimeZoneAbbreviation } from '@features/profile';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { darkBlue, lightGrey } from '@styles/colors';
import { light, medium } from '@styles/fontWeights';
import dayjs from 'dayjs';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { toast } from 'react-toastify';

import { OpenInterview } from '../../../../vectors/index';
import DeleteInterviewModal from '../DeleteInterviewModal/DeleteInterviewModal';
import UpdateInterviewInvitationModal from '../UpdateInterviewInvitationModal/UpdateInterviewInvitationModal';

type ApplicantEventInfoModalProps = {
  event: InterviewBlock | UserEvent | null;
  handleClose: () => void;
  setSelectedApplication: Dispatch<SetStateAction<Application | null>>;
  selectedApplication: Application | null;
};

const ApplicantEventInfoModal: FC<ApplicantEventInfoModalProps> = ({
  event,
  handleClose,
  setSelectedApplication,
  selectedApplication,
}) => {
  const { authData } = useContext(authContext);
  const browserTimeZone = getTimeZoneAbbreviation();
  const [
    isUpdateInterviewInvitationModalOpen,
    setIsUpdateInterviewInvitationModalOpen,
  ] = useState(false);
  const [isDeleteInterviewModalOpen, setIsDeleteInterviewModalOpen] =
    useState(false);
  const [isSelectInterviewStyleModalOpen, setIsSelectInterviewStyleModalOpen] =
    useState(false);
  const [isDateRangePickerShown, setIsDateRangePickerShown] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [startTimestamp, setStartTimestamp] = useState<number | null>(null);
  const [endTimestamp, setEndTimestamp] = useState<number | null>(null);
  const [formattedDate, setFormattedDate] = useState<string>('');
  const [selectedAction, setSelectedAction] =
    useState<ApplicationAction | null>(null);
  const isRescheduleInterview = true;
  const { inviteToInterviewBlocks, isInviteToInterviewBlocksLoading } =
    useInviteToInterviewBlocks({ setErrorText });

  const onInterviewInviteSuccess = () => {
    setIsDateRangePickerShown(false);
    setIsSelectInterviewStyleModalOpen(false);
    setSelectedAction(null);
    setStartTimestamp(null);
    setEndTimestamp(null);
    setFormattedDate('');
    setSelectedApplication(null);
    handleClose();
  };

  const { inviteToInterview, isInviteToInterviewLoading } =
    useInviteToInterview({ setErrorText, onSuccess: onInterviewInviteSuccess });

  const updateRating = (id: number, newRating: number) => {
    if (selectedApplication && selectedApplication.id === id) {
      setSelectedApplication((prevApp) =>
        prevApp ? { ...prevApp, rating: newRating } : null,
      );
    }
  };

  const [defaultType, setDefaultType] = useState<
    'inPerson' | 'virtual' | 'hireflix'
  >('inPerson');

  const handleInviteToInterviewSubmit = (message: string) => {
    if (isDateRangePickerShown && selectedApplication) {
      return inviteToInterview(
        {
          block_type: defaultType === 'inPerson' ? 1 : 0,
          physical_interview_address:
            authData?.user?.company?.additional_info
              ?.physical_interview_address,
          physical_interview_location:
            authData?.user?.company?.additional_info
              ?.physical_interview_location,
          virtual_interview_link:
            authData?.user?.company?.additional_info?.virtual_interview_link,
          invitee_id: selectedApplication.applicant.id,
          application_id: selectedApplication.id,
          body: message,
          start_time: startTimestamp && startTimestamp,
          end_time: endTimestamp && endTimestamp,
          eventDate: formattedDate,
          position: selectedApplication.position,
          ...(isRescheduleInterview
            ? { is_reschedule: isRescheduleInterview }
            : {}),
        },
        {
          onSuccess: (data) => {
            if (data?.status == 200) {
              toast.success('Interview invite sent');
              onInterviewInviteSuccess();
            }
          },
        },
      );
    }
    if (selectedApplication) {
      return inviteToInterviewBlocks(
        {
          block_type: defaultType === 'inPerson' ? 1 : 0,
          physical_interview_address:
            authData?.user?.company?.additional_info
              ?.physical_interview_address,
          physical_interview_location:
            authData?.user?.company?.additional_info
              ?.physical_interview_location,
          virtual_interview_link:
            authData?.user?.company?.additional_info?.virtual_interview_link,
          user_id: selectedApplication.applicant.id,
          application_id: selectedApplication.id,
          body: message,
          ...(selectedAction?.actionType === 'Invite to Open Interview' && {
            slot_interview: false,
          }),
          ...(selectedAction?.actionType === 'invite to interview block' && {
            slot_interview: true,
          }),
          ...(isRescheduleInterview
            ? { is_reschedule: isRescheduleInterview }
            : {}),
        },
        {
          onSuccess: (data) => {
            if (data?.status == 200) {
              toast.success('Interview invite sent');
              onInterviewInviteSuccess();
            }
          },
        },
      );
    }
    if (selectedApplication && defaultType === 'hireflix') {
      console.log('defaultType === hireflix', defaultType, selectedApplication);
    }
  };

  const handleSend = (isMessageRequired: boolean, message: string) => {
    switch (selectedAction?.actionType) {
      case 'invite to interview block':
      case 'Invite to Open Interview':
        return handleInviteToInterviewSubmit(message);
      case 'Scheduled Interview':
        return handleInviteToInterviewSubmit(message);
      case 'invite to hireflix interview':
        return handleInviteToInterviewSubmit(message);
      default:
    }
  };

  const { enterpriseCreateBulkAction } = useEnterpriseBulkAction({
    setErrorText,
  });

  const handleSendEnterprise = async (
    isMessageRequired: Boolean,
    message: string,
  ) => {
    const bulkActionPayload = {
      action: selectedAction?.actionType.toString(),
      ids: selectedApplication?.id?.toString() || '',
      ...(isMessageRequired && { message }),
      ...(isRescheduleInterview
        ? { is_reschedule: isRescheduleInterview }
        : {}),
      ...(isDateRangePickerShown &&
        selectedAction?.actionType === 'Scheduled Interview' && {
          start_time: startTimestamp && startTimestamp,
          end_time: endTimestamp && endTimestamp,
          eventDate: formattedDate,
        }),
      ...(selectedAction?.actionType === 'Invite to Open Interview' && {
        slot_interview: false,
      }),
      ...(selectedAction?.actionType === 'invite to interview block' && {
        slot_interview: true,
      }),
    };
    enterpriseCreateBulkAction(bulkActionPayload, {
      onSuccess: (data) => {
        if (data?.status == 200) {
          toast.success('Interview invite sent');
          onInterviewInviteSuccess();
        }
      },
    });
  };

  // const physical_address_concate =
  //   authData &&
  //   authData?.user.company?.additional_info?.physical_interview_location
  //     ? authData?.user.company?.additional_info?.physical_interview_location
  //     : authData
  //       ? `${authData?.user?.company?.s_address1 || authData?.user?.company?.s_address2 || ''} ${authData?.user?.company?.s_city || ''} ${authData?.user?.company?.s_state || ''} ${authData?.user?.company?.s_zip || ''}`.trim()
  //       : '';

  const companyInfo = event?.company;

  const physicaladdress =
    companyInfo?.additional_info?.physical_interview_location ||
    `${companyInfo?.s_address1 || companyInfo?.s_address2 || ''} ${companyInfo?.s_city || ''} ${companyInfo?.s_state || ''} ${companyInfo?.s_zip || ''}`.trim();

  const renderOpenInterviewsLayout = (event: any) => {
    return (
      <Stack spacing={3} sx={{ marginBottom: 1 }}>
        <List>
          <Box sx={{ pl: '20px' }}>
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: '5px',
                width: '100%',
              }}
              textAlign="center"
              alignSelf="center"
            >
              <strong>Interview type :</strong>{' '}
              <span style={{ marginLeft: '4px' }}>
                {event?.block_type === 1 || event?.block?.block_type === 1
                  ? 'In Person'
                  : 'Virtual'}
              </span>
            </Typography>

            {event?.block_type === 1 || event?.block?.block_type === 1 ? (
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '4px',
                  width: '100%',
                }}
                textAlign="center"
                alignSelf="center"
              >
                <LocationOnOutlinedIcon
                  fontSize="medium"
                  sx={{ mr: 1, color: 'black' }}
                />

                <span style={{ paddingLeft: '10px' }}>
                  {/* {
                    authData?.user?.company?.additional_info
                      ?.physical_interview_location
                  } */}
                  {physicaladdress}
                </span>
              </Typography>
            ) : (
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pl: '4px',
                  width: '100%',
                  marginTop: '4px',
                }}
                textAlign="center"
                alignSelf="center"
              >
                <VideocamOutlinedIcon
                  sx={{ mr: 2, color: 'black' }}
                  fontSize="medium"
                />

                <a
                  style={{ paddingLeft: '5px', textDecoration: 'none' }}
                  target="_blank"
                  href={
                    authData?.user?.company?.additional_info
                      ?.virtual_interview_link
                  }
                  rel="noreferrer"
                >
                  Join meeting
                </a>
              </Typography>
            )}
          </Box>
          <ListItem sx={{ p: 0 }}>
            <ListItemButton
              onClick={() => setSelectedApplication(event.application)}
              sx={{ width: '100%' }}
            >
              <Stack
                sx={{ width: '100%' }}
                spacing={3}
                justifyContent="space-between"
                alignItems="center"
                direction="row"
              >
                <Stack spacing={3} alignItems="center" direction="row">
                  <Avatar
                    src={(event.application.applicant.photo as string) || ''}
                    sx={{ width: 50, height: 50 }}
                  />
                  <Stack spacing={0}>
                    <Typography variant="subtitle1">
                      {event.application.applicant.name}
                    </Typography>
                    <Typography variant="body1">
                      {event.application.position}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  spacing={3}
                  alignItems="center"
                  direction="column"
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h2" component="p" fontWeight={medium}>
                      {dayjs(
                        event?.block?.dt_start_time ?? event?.dt_start_time,
                      ).format('DD')}
                    </Typography>
                    <Typography variant="h4" component="p" fontWeight={light}>
                      {dayjs(
                        event?.block?.dt_start_time ?? event?.dt_start_time,
                      ).format('MMM')}
                    </Typography>
                  </Stack>
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      display: 'inline-block',
                      fontSize: '0.8rem',
                      backgroundColor: lightGrey,
                      color: darkBlue,
                      padding: '0.5rem 1rem',
                      marginBottom: '5px !important',
                      borderRadius: 5,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: event.slot
                        ? `${dayjs(event?.slot?.slot_start_time).format('hh:mm')} - ${dayjs(event?.slot?.slot_end_time).format('hh:mm a')} ${browserTimeZone || ''}`
                        : `${dayjs(event?.block?.dt_start_time ?? event?.dt_start_time).format('hh:mm')} - ${dayjs(event?.block?.dt_end_time ?? event?.dt_end_time).format('hh:mm a')} ${browserTimeZone || ''}`,
                    }}
                  />
                </Stack>
              </Stack>
            </ListItemButton>
          </ListItem>
          <Stack
            spacing={2}
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ width: '100%' }}
            alignItems="center"
            justifyContent="center"
            marginTop={2}
          >
            <Button
              onClick={() => {
                setIsSelectInterviewStyleModalOpen(true);
                setSelectedApplication(event.application);
              }}
              variant="contained"
              startIcon={<EditIcon />}
              fullWidth
            >
              Reschedule
            </Button>

            <Button
              onClick={() => setIsDeleteInterviewModalOpen(true)}
              variant="contained"
              color="error"
              startIcon={<ClearIcon />}
              fullWidth
            >
              Delete
            </Button>
          </Stack>
        </List>
      </Stack>
    );
  };

  if (!event) {
    return null;
  }

  return (
    <>
      <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <br />
          <OpenInterview />
          <br />
          <Typography variant="h4" sx={{ p: 3 }}>
            Scheduled Interview
          </Typography>
        </DialogTitle>
        <DialogContent>
          {renderOpenInterviewsLayout(event as any)}
        </DialogContent>
      </Dialog>
      <UpdateInterviewInvitationModal
        isOpen={isUpdateInterviewInvitationModalOpen}
        handleClose={() => setIsUpdateInterviewInvitationModalOpen(false)}
        onSuccess={handleClose}
        userEvent={event as UserEvent}
      />
      <DeleteInterviewModal
        isOpen={isDeleteInterviewModalOpen}
        handleClose={() => setIsDeleteInterviewModalOpen(false)}
        onSuccess={handleClose}
        interview={event as unknown as Interview}
      />
      {selectedApplication && !isSelectInterviewStyleModalOpen && (
        <ApplicationViewModal
          handleClose={() => setSelectedApplication(null)}
          application={selectedApplication}
          searchValue=""
          setApplication={setSelectedApplication}
          updateRating={updateRating}
        />
      )}
      {isSelectInterviewStyleModalOpen && selectedApplication ? (
        <ChooseInterviewStyleModal
          isOpen={isSelectInterviewStyleModalOpen}
          handleClose={() => {
            setIsSelectInterviewStyleModalOpen(false);
            setIsDateRangePickerShown(false);
            setSelectedApplication(null);
            setSelectedAction(null);
          }}
          subtitle={`${selectedApplication?.applicant?.name} - ${selectedApplication.position}`}
          companyId={selectedApplication?.job_listing?.company?.id}
          jobId={selectedApplication?.job_listing?.id}
          scheduleInterview={() => {
            setSelectedAction(
              applicationActions.find(
                (item) => item.actionType === 'Scheduled Interview',
              )!,
            );
            setIsDateRangePickerShown(true);
          }}
          inviteToInterview={(inviteType: number) => {
            const actionType =
              inviteType === 0
                ? 'invite to interview block'
                : 'Invite to Open Interview';

            const action = applicationActions.find(
              (item) => item.actionType === actionType,
            );

            if (action) {
              setSelectedAction(action);
            }
          }}
        />
      ) : null}

      {selectedApplication && selectedAction && (
        <SingleMessageModal
          setDefaultType={setDefaultType}
          defaultType={defaultType}
          isOpen={Boolean(selectedAction)}
          handleClose={() => {
            setSelectedAction(null);
            setIsDateRangePickerShown(false);
            setStartTimestamp(null);
            setEndTimestamp(null);
            setFormattedDate('');
          }}
          action={selectedAction!}
          selectedCompanyId={event.company.id}
          title={getTitle(selectedAction!)}
          subtitle={`${selectedApplication?.applicant?.name} - ${selectedApplication.position}`}
          isSubtitleShown
          submitWithMessageText={getSendWithMessageButtonText(selectedAction!)}
          submitWithoutMessageText={getSendWithoutMessageButtonText(
            selectedAction!,
          )}
          showSendWithoutMessage={selectedAction?.showSendWithoutMessage}
          errorText={errorText}
          setErrorText={setErrorText}
          buttonColor="secondary"
          handleSubmit={
            authData.user?.enterprise ? handleSendEnterprise : handleSend
          }
          disabled={
            isInviteToInterviewBlocksLoading ||
            isInviteToInterviewLoading ||
            (isDateRangePickerShown && (!startTimestamp || !endTimestamp))
          }
          isDateRangePickerShown={isDateRangePickerShown}
          setStartTimestamp={setStartTimestamp}
          setEndTimestamp={setEndTimestamp}
          setFormattedDate={setFormattedDate}
        />
      )}
    </>
  );
};

export default ApplicantEventInfoModal;
